<template>
    <div>
        <b-datatable
            :key="datatableKey"
            :columns="columns"
            :endpoint="listRoute"
            :hidden-columns="hiddenColumns"
            :initial-filters="initialFilters"
            has-filter-selector
            item-key="id"
        >
            <template #buttons>
                <div class="px-2">
                    <v-tooltip top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                v-if="can('api.v1.consultants.store') && can('consultants.create')"
                                color="primary"
                                icon
                                v-bind="attrs"
                                :href="createItemUrl()"
                                v-on="on"
                            >
                                <v-icon>mdi-plus-circle</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('consultants::button.create') }}</span>
                    </v-tooltip>
                </div>
            </template>
            <template #filters="{ filters }">
                <v-col cols="4" class="pb-0">
                    <b-filter-input
                        v-model="filters.filters.name"
                        :label="t('consultants::label.name')"
                    ></b-filter-input>
                </v-col>
                <v-col cols="4">
                    <b-filter-input
                        v-model="filters.filters.dms_id"
                        :label="t('consultants::label.dms_id')"
                    ></b-filter-input>
                </v-col>
                <v-col cols="4">
                    <b-filter-input
                        v-model="filters.filters.gmc_number"
                        :label="t('consultants::label.gmc_id')"
                    ></b-filter-input>
                </v-col>
                <v-col cols="4" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.is_registered"
                        :items="filtersOptions.is_registered"
                        :label="t('consultants::label.registered')"
                        :clearable="filters.filters.is_registered !== null"
                    >
                    </b-filter-select>
                </v-col>
            </template>

            <template #[`item.is_registered`]="{ item }">
                <div class="grid-status">
                    <span>
                        {{ item.is_registered ? t('default::label.yes') : t('default::label.no') }}
                    </span>
                </div>
            </template>

            <template #[`item.name`]="{ item }">
                <div v-if="can('consultants.get')" class="link" @click="$root.goTo(editItemUrl(item))">
                    {{ item.name }}
                </div>
                <div v-else>
                    {{ item.name }}
                </div>
            </template>

            <template #[`item.actions`]="{ item }">
                <div class="table-actions">
                    <v-tooltip v-if="can('consultants.export-record-as-pdf')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="exportRecordAsPdf(item)"
                            >
                                <v-icon size="24">mdi-file-export-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('default::button.export_record_as_pdf') }}</span>
                    </v-tooltip>

                    <v-tooltip v-if="can('api.v1.consultants.update')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(editItemUrl(item))"
                            >
                                <v-icon>mdi-pencil</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('users::button.edit') }}</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="!can('api.v1.consultants.update') && can('api.v1.consultants.show')"
                        top
                        transition="v-fab-transition"
                    >
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(editItemUrl(item))"
                            >
                                <v-icon>mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('consultants::button.details') }}</span>
                    </v-tooltip>
                    <b-delete
                        v-if="can('api.v1.consultants.destroy')"
                        :path="deleteItemUrl(item)"
                        :hide-true-btn="!!item.count_active_patients"
                        :text="
                            !!item.count_active_patients
                                ? t('consultants::confirmation.have_active_users_content')
                                : t('default::confirmation.deletion')
                        "
                        :title="
                            !!item.count_active_patients
                                ? t('consultants::confirmation.have_active_users_title')
                                : t('default::confirmation.general')
                        "
                        icon="mdi-trash-can"
                        @deleted="triggerDeleted"
                    >
                    </b-delete>
                </div>
            </template>
        </b-datatable>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
        initialFilters: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            datatableKey: 0,
            downloadingFile: false,
            hiddenColumns: ['id'],
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('consultants::label.consultant_name'),
                    value: 'name',
                    width: 200,
                    sortable: true,
                },
                {
                    text: t('consultants::label.dms_id'),
                    value: 'dms_id',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('consultants::label.gmc_id'),
                    value: 'gmc_number',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('consultants::label.registered'),
                    value: 'is_registered',
                    width: 140,
                    sortable: true,
                },
                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: 'count_active_patients',
                    width: 180,
                    align: 'end',
                },
            ],
            filtersOptions: {
                is_registered: [
                    {
                        text: t('default::options.all'),
                        value: null,
                    },
                    {
                        text: t('default::options.yes'),
                        value: 1,
                    },
                    {
                        text: t('default::options.no'),
                        value: 0,
                    },
                ],
            },
        };
    },
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('consultants.export-record-as-pdf', { consultant: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        deleteItemUrl(item) {
            return route('api.v1.consultants.destroy', { consultant: item.id });
        },
        triggerDeleted() {
            this.datatableKey++;
            this.$root.notify(
                'info',
                t('default::notification.success'),
                t('consultants::notification.consultant.deleted')
            );
        },
        itemUrl(item) {
            return route('consultants.get', { id: item.id });
        },
        editItemUrl(item) {
            return route('consultants.get', { id: item.id });
        },
        createItemUrl() {
            return route('consultants.create');
        },
    },
};
</script>
